"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative w-full bg-[#264256] overflow-hidden min-h-[40vh] md:min-h-[50vh] flex items-center">
      <div className="container mx-auto px-6 max-w-7xl relative z-10 py-10 md:py-16">
        <div ref={ref} className={cn("flex flex-col items-center text-center gap-4 md:gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <span className="font-default text-[12px] text-[#65d19d] uppercase tracking-widest font-semibold">Contact</span>
          <h1 className="font-default font-bold text-4xl md:text-[60px] leading-[1.1] text-white max-w-4xl">
            Get in touch.
          </h1>
          <p className="font-default text-white/80 text-base md:text-xl leading-relaxed max-w-2xl">
            We respond within one business day. For urgent enquiries, call us directly.
          </p>
        </div>
      </div>
    </section>;
}
function ContactSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-background py-12 md:py-20">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-start">

            <div className="w-full lg:w-[45%] flex flex-col gap-6 md:gap-8">
              <div className="flex flex-col gap-5">
                <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">Clinic Agatas</h2>
                <div className="flex flex-col gap-3 md:gap-4">

                  <div className="flex items-start gap-4 p-4 md:p-5 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
                    <div className="bg-white rounded-full p-2 shrink-0">
                      <MapPin className="w-4 h-4 text-[#65d19d]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider">Kaunas Clinic</span>
                      <span className="font-default text-[#264256] text-sm">Linkuvos st. 60, Kaunas, Lithuania</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 md:p-5 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
                    <div className="bg-white rounded-full p-2 shrink-0">
                      <Phone className="w-4 h-4 text-[#65d19d]" />
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                      <span className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider">Phone</span>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-default text-[#264256] text-xs text-muted-foreground uppercase tracking-wider">Lithuania</span>
                          <a href="tel:+37060226118" className="font-default text-[#264256] text-sm hover:underline">+370 602 26118</a>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="font-default text-[#264256] text-xs text-muted-foreground uppercase tracking-wider">For Irish patients</span>
                          <a href="tel:+353871753061" className="font-default text-[#264256] text-sm hover:underline">+353 871 753 061</a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 md:p-5 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
                    <div className="bg-white rounded-full p-2 shrink-0">
                      <Mail className="w-4 h-4 text-[#65d19d]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider">Email</span>
                      <a href="mailto:info@clinicagatas.com" className="font-default text-[#264256] text-sm hover:underline">info@clinicagatas.com</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 md:p-5 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
                    <div className="bg-white rounded-full p-2 shrink-0">
                      <Clock className="w-4 h-4 text-[#65d19d]" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider">Opening Hours</span>
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between gap-4 md:gap-8">
                          <span className="font-default text-[#264256] text-sm">Monday to Friday</span>
                          <span className="font-default text-[#264256] text-sm font-bold">09:00 - 18:00 LT</span>
                        </div>
                        <div className="flex justify-between gap-4 md:gap-8">
                          <span className="font-default text-muted-foreground text-xs">Irish time (summer)</span>
                          <span className="font-default text-muted-foreground text-xs">07:00 - 16:00 IST</span>
                        </div>
                        <div className="flex justify-between gap-4 md:gap-8">
                          <span className="font-default text-muted-foreground text-xs">Irish time (winter)</span>
                          <span className="font-default text-muted-foreground text-xs">07:00 - 16:00 GMT</span>
                        </div>
                        <div className="flex justify-between gap-4 md:gap-8 mt-1">
                          <span className="font-default text-[#264256] text-sm">Saturday - Sunday</span>
                          <span className="font-default text-[#264256] text-sm font-bold">Closed</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div className="w-full lg:w-[55%]">
              <div className="rounded-2xl overflow-hidden border border-[#a9e5c9] shadow-lg w-full h-[320px] md:h-[520px]">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2312.4!2d23.8862901!3d54.9128656!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46e722bdd2de44d7%3A0xac022da03e371c88!2sClinic%20Agatas!5e0!3m2!1sen!2slt!4v1" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Clinic Agatas location" />
              </div>
              <p className="font-default text-muted-foreground text-xs mt-3">Linkuvos st. 60, Kaunas, Lithuania. Free parking available on site.</p>
            </div>

          </div>
        </div>
      </div>
    </section>;
}
export default function ContactezNosExperts() {
  return <>
      <Hero />
      <ContactSection />
    </>;
}